/**
 * React bindings published at the `@emilgroup/flags/react` subpath. They cover
 * both the flagservice **capability** API (defined here) and the OpenFeature
 * **flag** bindings (re-exported from `./react-flags`: {@link FlagsProvider} and
 * the `useBooleanFlag` / `useStringFlag` / … hooks). Apps consume both from one
 * place and never import OpenFeature directly.
 *
 * The capability provider resolves the caller's capabilities once via
 * `GET /flagservice/v1/capabilities` and exposes them through context + hooks,
 * plus a {@link CapabilitiesDebugPanel} for inspecting the catalog and resolved
 * values in the current environment.
 *
 * Both providers are parameterized (they take the same options as
 * {@link FlagServiceClient}, e.g. a `token` getter and `environmentKey`), so any
 * app can reuse them by wiring its own token source — no app-specific coupling.
 */
import * as React from "react";
import {
  QueryClientProvider,
  useQuery,
  type QueryClient,
} from "@tanstack/react-query";

import type { FlagStore } from "./cache";
import {
  capabilitiesQueryKey,
  getCapabilitiesQueryClient,
  whenCapabilitiesRestored,
} from "./capabilities-query";
import { FlagServiceClient } from "./client";
import { localStorageToken } from "./local-storage-token";
import {
  disabledCapability,
  knownCapabilityKeys,
  type Capability,
  type CapabilityKey,
  type CapabilitySet,
  type KnownCapabilityKey,
  normalizeCapabilitySet,
} from "./capabilities";
import type { EmilFlagsClientOptions } from "./types";
import type { WireCapabilityDefinition, WireEvaluationContext } from "./wire";

interface CapabilitiesContextValue {
  /** Resolved capabilities, or `undefined` until the first resolve settles. */
  capabilities: CapabilitySet | undefined;
  /** True once the first resolve has settled (success or failure). */
  ready: boolean;
  /** Re-resolve capabilities (e.g. after a tenant/role change). */
  refresh: () => void;
  /** The client used to resolve, exposed for the debug panel / definitions. */
  client: FlagServiceClient | undefined;
  /** Environment the capabilities are resolved against. */
  environmentKey: string | undefined;
}

const CapabilitiesContext = React.createContext<CapabilitiesContextValue | undefined>(
  undefined,
);

// Dedicated react-query context for the capability client. Passing it to both
// the `QueryClientProvider` and the `useQuery` below keeps the flags client
// fully private: the default react-query context — and with it every
// `useQuery` a host app mounts under this provider — still resolves to the
// app's own `QueryClient` instead of the persisted flags singleton.
const CapabilitiesQueryContext = React.createContext<QueryClient | undefined>(
  undefined,
);

// Default token source when an app doesn't pass `token`: read the active
// account's bearer token from localStorage (`APP_TOKEN.accessToken`). Apps that
// keep their token elsewhere pass their own `token`; pass `() => undefined` to
// send no token at all.
const defaultToken = localStorageToken();

export interface CapabilitiesProviderProps extends EmilFlagsClientOptions {
  /**
   * Optional evaluation context for per-user targeting / percentage rollouts.
   * The tenant itself is always derived server-side from the bearer token.
   */
  context?: WireEvaluationContext;
  /**
   * Optional stable identifier for the signed-in tenant (e.g. a tenant slug or
   * id). Folded into the cache key so two tenants on the same browser/origin can
   * never read each other's resolved capabilities from the shared cache.
   *
   * The cache is otherwise keyed only by the build-time environment — **not** by
   * the bearer token — so without this, a logout→login as a different tenant can
   * serve the previous tenant's snapshot until it goes stale. Pass the current
   * tenant here, or clear the cache on logout with `resetCapabilitiesQueryClient`.
   * Changing it re-resolves against the new tenant's own cache bucket.
   */
  tenantId?: string;
  /**
   * Called when a resolve fails. The provider still **fails open** (reveals
   * all-disabled defaults) so a flagservice outage never blocks the app.
   */
  onError?: (error: unknown) => void;
  /**
   * Time (ms) a cached snapshot is served before it's revalidated. Defaults to
   * 60s — the previous cache TTL. Applied to this provider's query.
   */
  staleTime?: number;
  /** Time (ms) an unused snapshot stays in memory. Defaults to 24h. */
  cacheTime?: number;
  /**
   * Hard cache-expiry (ms): a persisted snapshot older than this is dropped on
   * restore instead of being served. Defaults to 24h. Persistence is set up once
   * per process, so this only takes effect on the first provider mounted.
   */
  maxAge?: number;
  /**
   * @deprecated No longer used for capabilities — caching, de-duplication and
   * refresh are handled by TanStack Query (see {@link getCapabilitiesQueryClient}).
   * Still accepted so existing call sites keep compiling, but ignored here. The
   * flags track (`FlagsProvider`) continues to use it.
   */
  store?: FlagStore;
  children?: React.ReactNode;
}

/**
 * Resolves the caller's capabilities and provides them to the React tree. Mount
 * it where a bearer token is already available (e.g. inside your authenticated
 * subtree); it resolves on mount and whenever the environment/context change or
 * {@link CapabilitiesContextValue.refresh} is called. If the token only becomes
 * available later, call `refresh()` at that point.
 *
 * Caching is backed by a process-wide TanStack Query client persisted to
 * localStorage (see {@link getCapabilitiesQueryClient}); this provider supplies
 * the {@link QueryClientProvider} so consumers need no extra wiring. The client
 * lives in its own react-query context ({@link CapabilitiesQueryContext}), so
 * the host app's queries — even when mounted under this provider — keep using
 * the app's own `QueryClient` and never end up in the persisted flags snapshot.
 */
export function CapabilitiesProvider(
  props: CapabilitiesProviderProps,
): React.ReactElement {
  const { staleTime, cacheTime, maxAge } = props;
  // The client is a process-wide singleton; this returns it (creating it on the
  // first call), so calling on every render is cheap and the reference is stable.
  const queryClient = getCapabilitiesQueryClient({ staleTime, cacheTime, maxAge });

  return (
    <QueryClientProvider client={queryClient} context={CapabilitiesQueryContext}>
      <CapabilitiesProviderInner {...props} />
    </QueryClientProvider>
  );
}

/**
 * The data half of {@link CapabilitiesProvider}, rendered inside the
 * {@link QueryClientProvider} so it can call {@link useQuery}.
 */
function CapabilitiesProviderInner(
  props: CapabilitiesProviderProps,
): React.ReactElement {
  const {
    children,
    context,
    onError,
    baseUrl,
    clientSdkKey,
    environmentKey,
    token,
    fetch: fetchImpl,
    timeoutMs,
    staleTime,
    cacheTime,
    tenantId,
  } = props;

  // `token`/`onError` may have a fresh identity each render; read them through
  // refs so they don't rebuild the client / re-run effects while always using
  // the latest value.
  const tokenRef = React.useRef(token);
  tokenRef.current = token;
  const onErrorRef = React.useRef(onError);
  onErrorRef.current = onError;

  // Build the client from the primitive options. `token` is read via the ref so
  // a fresh getter identity each render doesn't rebuild the client / refetch.
  const client = React.useMemo<FlagServiceClient | undefined>(() => {
    try {
      return new FlagServiceClient({
        baseUrl,
        clientSdkKey,
        environmentKey,
        timeoutMs,
        fetch: fetchImpl,
        token: () => {
          const current = tokenRef.current;
          if (current == null) return defaultToken();
          return typeof current === "function" ? current() : current;
        },
      });
    } catch {
      return undefined;
    }
  }, [baseUrl, clientSdkKey, environmentKey, timeoutMs, fetchImpl]);

  const contextKey = context?.key;
  const contextAttributes = context?.attributes;
  const wireContext: WireEvaluationContext | undefined =
    contextKey || contextAttributes
      ? { key: contextKey, attributes: contextAttributes }
      : undefined;
  const envId = clientSdkKey ?? environmentKey ?? "";

  // Wait for the persisted cache to be restored before the query is allowed to
  // fetch. Restoration is async (see `whenCapabilitiesRestored`); without this
  // gate the first query races the restore and always hits the network even when
  // a fresh snapshot is in localStorage.
  const [restored, setRestored] = React.useState(false);
  React.useEffect(() => {
    let cancelled = false;
    const done = () => {
      if (!cancelled) setRestored(true);
    };
    // Settle on both success and failure — a failed restore must not block fetching.
    whenCapabilitiesRestored().then(done, done);
    return () => {
      cancelled = true;
    };
  }, []);

  const query = useQuery({
    context: CapabilitiesQueryContext,
    queryKey: capabilitiesQueryKey(envId, wireContext, tenantId),
    queryFn: async () => {
      // `enabled` gates on `client`, so it is defined whenever this runs.
      const response = await (client as FlagServiceClient).getCapabilities(wireContext);
      return normalizeCapabilitySet(response.capabilities);
    },
    enabled: client != null && restored,
    // Only forward these when the app actually set them. Passing `undefined`
    // here would *override* the QueryClient defaults (the merge is a shallow
    // spread with per-query options last), collapsing `staleTime` to 0 — which
    // makes every reload refetch even with a fresh persisted snapshot.
    ...(staleTime !== undefined ? { staleTime } : {}),
    ...(cacheTime !== undefined ? { cacheTime } : {}),
  });

  // Fail open: report errors (misconfiguration, or a failed resolve) via
  // `onError`, but keep serving whatever the query holds — `undefined` on the
  // first failure (every capability disabled), or the last good snapshot on a
  // background refetch failure.
  const queryError = query.error;
  React.useEffect(() => {
    if (!client) {
      onErrorRef.current?.(
        new Error("@emilgroup/flags: clientSdkKey or environmentKey is required"),
      );
      return;
    }
    if (queryError) {
      onErrorRef.current?.(queryError);
    }
  }, [client, queryError]);

  const refresh = React.useCallback(() => {
    void query.refetch();
  }, [query]);

  // Without a client the query never runs (stuck loading), so report ready so the
  // app isn't blocked — matching the previous fail-open behaviour. Otherwise we're
  // ready once the cache has been restored and the query is no longer loading
  // (restored fresh data settles it instantly; only a cold fetch shows loading).
  const ready = client ? restored && !query.isLoading : true;
  const capabilities = query.data;

  const value = React.useMemo<CapabilitiesContextValue>(
    () => ({ capabilities, ready, refresh, client, environmentKey }),
    [capabilities, ready, refresh, client, environmentKey],
  );

  return (
    <CapabilitiesContext.Provider value={value}>
      {children}
    </CapabilitiesContext.Provider>
  );
}

function useCapabilitiesContext(): CapabilitiesContextValue {
  const ctx = React.useContext(CapabilitiesContext);
  if (!ctx) {
    throw new Error(
      "useCapability/useCapabilities must be used within <CapabilitiesProvider>.",
    );
  }
  return ctx;
}

/** Whether the capabilities have finished resolving (success or failure). */
export function useCapabilitiesReady(): boolean {
  return useCapabilitiesContext().ready;
}

/** The full resolved capability set (or `undefined` until ready). */
export function useCapabilities(): CapabilitySet | undefined {
  return useCapabilitiesContext().capabilities;
}

/**
 * Resolve a single capability for the current tenant. Returns a disabled
 * capability until resolution completes or if the capability is unknown.
 */
export function useCapability(key: CapabilityKey): Capability {
  const { capabilities } = useCapabilitiesContext();
  return capabilities?.[key] ?? disabledCapability;
}

/**
 * Returns a function that re-resolves capabilities now (a TanStack Query
 * refetch), bypassing the stale-time check — e.g. after a tenant/role change or
 * once a token becomes available. To clear the cache entirely (logout / tenant
 * switch) use {@link resetCapabilitiesQueryClient} instead.
 */
export function useRefreshCapabilities(): () => void {
  return useCapabilitiesContext().refresh;
}

interface CapabilityDefinitionsState {
  definitions: WireCapabilityDefinition[];
  loading: boolean;
  error: unknown;
}

/**
 * Fetch the static capability catalog (`GET /capabilities/definitions`) — every
 * capability the platform can expose, independent of environment/tenant. Useful
 * for discovery / debugging.
 */
export function useCapabilityDefinitions(): CapabilityDefinitionsState {
  const { client } = useCapabilitiesContext();
  const [state, setState] = React.useState<CapabilityDefinitionsState>({
    definitions: [],
    loading: true,
    error: undefined,
  });

  React.useEffect(() => {
    let cancelled = false;
    if (!client) {
      setState({ definitions: [], loading: false, error: undefined });
      return;
    }
    setState((prev) => ({ ...prev, loading: true }));
    client
      .getCapabilityDefinitions()
      .then((definitions) => {
        if (!cancelled) {
          setState({ definitions, loading: false, error: undefined });
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setState({ definitions: [], loading: false, error });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [client]);

  return state;
}

function formatValue(value: unknown): string {
  if (value === null || value === undefined) {
    return "—";
  }
  if (typeof value === "object") {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value);
}

/**
 * A feature flag value to display in the debug panel. OpenFeature flags can't be
 * enumerated from the client, so the host app resolves a known set (with its own
 * OpenFeature client) and passes them in.
 */
export interface DebugFlag {
  key: string;
  value: unknown;
  /** Optional value type label (e.g. "boolean", "string"). */
  type?: string;
  /** Optional OpenFeature resolution reason (e.g. "TARGETING_MATCH", "DEFAULT"). */
  reason?: string;
}

export interface CapabilitiesDebugPanelProps {
  /** Whether the panel starts expanded. Defaults to collapsed. */
  defaultOpen?: boolean;
  /** Heading shown in the panel. */
  title?: string;
  /**
   * Optional OpenFeature flags to show in a second section. The host app
   * resolves these (capabilities are auto-discovered; OpenFeature flags are not).
   */
  flags?: DebugFlag[];
}

const panelStyles: Record<string, React.CSSProperties> = {
  root: {
    position: "fixed",
    bottom: 16,
    right: 16,
    zIndex: 2147483000,
    fontFamily:
      "ui-monospace, SFMono-Regular, Menlo, Consolas, 'Liberation Mono', monospace",
    fontSize: 12,
    color: "#1b1b1f",
  },
  toggle: {
    background: "#1b5e36",
    color: "#fff",
    border: "none",
    borderRadius: 999,
    padding: "8px 14px",
    cursor: "pointer",
    boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
    fontSize: 12,
    fontWeight: 600,
  },
  panel: {
    width: 460,
    maxWidth: "90vw",
    maxHeight: "70vh",
    overflow: "auto",
    background: "#fff",
    border: "1px solid #d0d7de",
    borderRadius: 8,
    boxShadow: "0 8px 28px rgba(0,0,0,0.28)",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
    padding: "10px 12px",
    borderBottom: "1px solid #eaeef2",
    position: "sticky",
    top: 0,
    background: "#fff",
  },
  th: {
    textAlign: "left",
    padding: "6px 8px",
    borderBottom: "1px solid #eaeef2",
    color: "#57606a",
    fontWeight: 600,
    whiteSpace: "nowrap",
  },
  td: {
    padding: "6px 8px",
    borderBottom: "1px solid #f2f4f6",
    verticalAlign: "top",
    wordBreak: "break-word",
  },
  sectionLabel: {
    padding: "10px 12px 4px",
    fontWeight: 700,
    color: "#24292f",
    textTransform: "uppercase",
    letterSpacing: 0.4,
    fontSize: 11,
  },
};

const pillStyle = (on: boolean): React.CSSProperties => ({
  display: "inline-block",
  padding: "1px 8px",
  borderRadius: 999,
  fontWeight: 700,
  color: on ? "#0a5d2c" : "#8a1f11",
  background: on ? "#e7f8ee" : "#fdecea",
});

/**
 * A floating, collapsible panel that lists every capability the platform
 * exposes (the catalog from `GET /capabilities/definitions`) and its resolved
 * value for the current tenant + environment. Drop it anywhere inside a
 * {@link CapabilitiesProvider}; intended for non-production builds.
 */
export function CapabilitiesDebugPanel(
  props: CapabilitiesDebugPanelProps,
): React.ReactElement {
  const { defaultOpen = false, title = "Feature flags", flags = [] } = props;
  const [open, setOpen] = React.useState(defaultOpen);

  const capabilities = useCapabilities();
  const ready = useCapabilitiesReady();
  const { environmentKey, refresh } = useCapabilitiesContext();
  const { definitions, loading, error } = useCapabilityDefinitions();

  // Union of catalog keys and any resolved keys, so nothing is hidden.
  const rows = React.useMemo(() => {
    const byKey = new Map<string, { description?: string; valueType?: string }>();
    for (const def of definitions) {
      if (def.key) {
        byKey.set(def.key, { description: def.description, valueType: def.valueType });
      }
    }
    if (capabilities) {
      for (const key of Object.keys(capabilities)) {
        if (!byKey.has(key)) {
          byKey.set(key, {});
        }
      }
    }
    return Array.from(byKey.entries()).map(([key, meta]) => ({
      key,
      meta,
      resolved: capabilities
        ? (capabilities as Record<string, Capability>)[key]
        : undefined,
    }));
  }, [definitions, capabilities]);

  if (!open) {
    return (
      <div style={panelStyles.root}>
        <button
          type="button"
          style={panelStyles.toggle}
          onClick={() => setOpen(true)}
        >
          🚩 {title}
        </button>
      </div>
    );
  }

  return (
    <div style={panelStyles.root}>
      <div style={panelStyles.panel}>
        <div style={panelStyles.header}>
          <strong>
            🚩 {title}
            <span style={{ color: "#57606a", fontWeight: 400 }}>
              {" "}
              · env <code>{environmentKey ?? "?"}</code> ·{" "}
              {ready ? "resolved" : "resolving…"}
            </span>
          </strong>
          <span style={{ display: "flex", gap: 6 }}>
            <button
              type="button"
              style={{ ...panelStyles.toggle, background: "#57606a", padding: "4px 10px" }}
              onClick={refresh}
            >
              ↻
            </button>
            <button
              type="button"
              style={{ ...panelStyles.toggle, background: "#57606a", padding: "4px 10px" }}
              onClick={() => setOpen(false)}
            >
              ✕
            </button>
          </span>
        </div>

        {error ? (
          <div style={{ padding: "10px 12px", color: "#8a1f11" }}>
            Failed to load catalog: {String(error)}
          </div>
        ) : null}

        <div style={panelStyles.sectionLabel}>Capabilities (per-tenant)</div>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={panelStyles.th}>Capability</th>
              <th style={panelStyles.th}>Enabled</th>
              <th style={panelStyles.th}>Value</th>
              <th style={panelStyles.th}>Variation</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ key, meta, resolved }) => (
              <tr key={key}>
                <td style={panelStyles.td}>
                  <div style={{ fontWeight: 600 }}>{key}</div>
                  {meta.description ? (
                    <div style={{ color: "#57606a" }}>{meta.description}</div>
                  ) : null}
                  {meta.valueType ? (
                    <div style={{ color: "#8c959f" }}>type: {meta.valueType}</div>
                  ) : null}
                </td>
                <td style={panelStyles.td}>
                  <span style={pillStyle(Boolean(resolved?.enabled))}>
                    {resolved ? (resolved.enabled ? "on" : "off") : "—"}
                  </span>
                </td>
                <td style={panelStyles.td}>
                  <code>{resolved ? formatValue(resolved.value) : "—"}</code>
                </td>
                <td style={panelStyles.td}>
                  <code>{resolved?.variation || "—"}</code>
                </td>
              </tr>
            ))}
            {rows.length === 0 && !loading ? (
              <tr>
                <td style={panelStyles.td} colSpan={4}>
                  No capabilities found.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>

        {flags.length > 0 ? (
          <React.Fragment>
            <div style={panelStyles.sectionLabel}>OpenFeature flags</div>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={panelStyles.th}>Flag</th>
                  <th style={panelStyles.th}>Value</th>
                  <th style={panelStyles.th}>Reason</th>
                </tr>
              </thead>
              <tbody>
                {flags.map((flag) => (
                  <tr key={flag.key}>
                    <td style={panelStyles.td}>
                      <div style={{ fontWeight: 600 }}>{flag.key}</div>
                      {flag.type ? (
                        <div style={{ color: "#8c959f" }}>type: {flag.type}</div>
                      ) : null}
                    </td>
                    <td style={panelStyles.td}>
                      <code>{formatValue(flag.value)}</code>
                    </td>
                    <td style={panelStyles.td}>
                      <code>{flag.reason || "—"}</code>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </React.Fragment>
        ) : null}
      </div>
    </div>
  );
}

export type { Capability, CapabilityKey, CapabilitySet, KnownCapabilityKey };
export { knownCapabilityKeys };

// The capability cache internals, for advanced hosts that want to prefetch,
// invalidate, or clear capabilities directly (e.g. on logout / tenant switch).
export {
  getCapabilitiesQueryClient,
  capabilitiesQueryKey,
  resetCapabilitiesQueryClient,
  type CapabilitiesQueryClientOptions,
} from "./capabilities-query";

// The default token source, re-exported so apps can customize it (e.g. a
// different localStorage key) and pass it back as `token`.
export { localStorageToken, type LocalStorageTokenOptions } from "./local-storage-token";

// OpenFeature flag bindings (provider + hooks + <Feature>). Co-published under
// this subpath so apps read flags and capabilities from one place without ever
// importing OpenFeature directly.
export {
  FlagsProvider,
  Feature,
  useFlagsReady,
  useBooleanFlag,
  useStringFlag,
  useNumberFlag,
  useObjectFlag,
  useAllFlags,
} from "./react-flags";
export type { FlagsProviderProps, FeatureProps, FlagSnapshot } from "./react-flags";
